import cn from 'classnames';
import type { NextPage } from 'next';
import { useMemo } from 'react';

import { Layout } from '@sravni/react-design-system/lib/Layout';
import { Logo } from '@sravni/react-design-system/lib/Logo';

import type { Application } from '@src/@types/app';
import BottomBannerContainer from '@src/components/BottomBannerContainer';
import { PageContext } from '@src/config';
import { getSpecialPageListData } from '@src/config/getSpecialPageListData';
import type { IPageContextConfig } from '@src/config/PageContext';
import { BottomBannerTypes } from '@src/constants/bottomBannerTypes';
import { MODES } from '@src/constants/filters';
import { ListKeys } from '@src/constants/microcredits';
import { useListPage } from '@src/hooks/useListPage';
import { useSelectUser } from '@src/reducers/user';
import { isAffiliatesPageRoute } from '@src/utils/routing';
import { Layout as AppLayout } from 'app/Layout';
import { bankruptcyOfferAction } from 'entities/Microcredits/lib/sravniOfferAction';
import { CardActionProvider } from 'entities/Microcredits/ui/CardActionProvider';
import ProductList from 'features/ProductList/ui/ProductList/ProductList';
import { specialPageDisclaimers } from 'shared/config/specialPageDisclaimers';
import { TextList } from 'shared/ui';
import { BrandingHeaders } from 'widgets/BrandingHeaders';
import { isShowCalculatorByQuery } from 'widgets/FullDealEntryPoint/lib/isShowCalculatorByQuery';
import { LightweightCard } from 'widgets/LightweightCard';

import { getActions, useSpecialPageBanners } from '../lib';

import { CHECKER_QUERY, HEADER_QUERY, LOGO_AND_TEXT_QUERY, NOT_DISPLAY_VALUE } from './SpecialPage.config';
import styles from './SpecialPage.module.scss';

const pageConfig: IPageContextConfig = {
  listKey: ListKeys.SPEC_OFFERS,
  source: 'search_special',
  sortPanelVisible: false,
  seoLinksVisible: true,
  extraFiltersVisible: true,
  cardTermIsVisible: true,
  withBanners: true,
  withInvitationBanner: true,
  withEmailBurner: true,
  withSelectionBanner: true,
  withCardProductLink: true,
  withCardDetailsClick: true,
};

export const SpecialPage: NextPage = () => {
  const listKey = ListKeys.SPEC_OFFERS;
  const { filter, pathname, query } = useListPage(listKey);
  const { withCRMBanner, withMobileBanner, isCRMSpecial } = useSpecialPageBanners();
  const user = useSelectUser();

  const config = useMemo(() => ({ ...pageConfig, withMonetizationCardLogoClick: true, listKey }), [listKey]);

  const isShowCalculator = isShowCalculatorByQuery(query);

  const showLogoAndText = query?.[LOGO_AND_TEXT_QUERY] !== NOT_DISPLAY_VALUE;
  const showChecker = query?.[CHECKER_QUERY] !== NOT_DISPLAY_VALUE;
  const showHeaders = query?.[HEADER_QUERY] !== NOT_DISPLAY_VALUE;

  return (
    <AppLayout
      cookiePolicyDisabled
      footer={null}
      showMobileAppBanner={false}
      seoJsonSchemaConfig={{ listKey: pageConfig.listKey }}
      header={
        showLogoAndText && (
          <Layout>
            <Logo className={cn('h-mt-20')} />
          </Layout>
        )
      }
    >
      <PageContext.Provider value={config}>
        <BrandingHeaders
          subHeadingHidden
          showHeaders={showHeaders}
          containerClassName={cn(styles.header, {
            [styles.headerWithCrmBanner]: withCRMBanner,
            [styles.headerWithMobileBanner]: withMobileBanner,
            [styles.withoutChecker]: !showChecker,
          })}
          afterHeaders={getActions({
            isCRMSpecial,
            showChecker,
            isShowCalculator,
            showHeaders,
            user,
          })}
        />
        <CardActionProvider rules={[bankruptcyOfferAction]}>
          <ProductList
            filter={filter}
            pathname={pathname}
            gridLayout
            renderCard={(props) => <LightweightCard {...props} />}
          />
        </CardActionProvider>
        <BottomBannerContainer enabledTypes={[BottomBannerTypes.OUT_OF_PAGE]} />
      </PageContext.Provider>

      {showLogoAndText && (
        <Layout>
          <TextList className={styles.disclaimer} texts={specialPageDisclaimers} />
        </Layout>
      )}
    </AppLayout>
  );
};

SpecialPage.getInitialProps = async (ctx: Application.ReduxNextPageContext) => {
  const mode = isAffiliatesPageRoute(ctx.asPath) ? MODES.AFFILIATES : MODES.SPEC_OFFERS;

  await getSpecialPageListData(ctx, {
    key: ListKeys.SPEC_OFFERS,
    defaultFiltersMode: mode,
  });
};
